import { t } from '../i18n/translations';
import { ApiService } from '../services/api';
import { AudioAnalyzer } from '../services/audio';
import { MeetingInfo, JoinMeetingResponse } from '../types';

export class PreJoinRoom {
  private roomId: string;
  private hostToken?: string;
  private meetingInfo: MeetingInfo | null = null;
  private localStream: MediaStream | null = null;
  private audioAnalyzer: AudioAnalyzer = new AudioAnalyzer();

  private isAudioMuted = false;
  private isVideoMuted = false;
  private displayName = '';
  private passcode = '';
  private avatarUrl = '';
  private errorMessage = '';
  private isJoining = false;

  public onJoinSuccess?: (resp: JoinMeetingResponse, localStream: MediaStream | null, isVideoMuted?: boolean, isAudioMuted?: boolean) => void;
  public onBack?: () => void;

  constructor(roomId: string, hostToken?: string) {
    this.roomId = roomId;
    this.hostToken = hostToken || localStorage.getItem(`verve_host_${roomId}`) || undefined;

    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('name')) {
        this.displayName = params.get('name')!;
      }
      if (params.get('token')) {
        this.hostToken = params.get('token')!;
      }
      if (params.get('avatar')) {
        this.avatarUrl = params.get('avatar')!;
      }
    } catch (_) {}
  }

  public async init(container: HTMLElement) {
    try {
      this.meetingInfo = await ApiService.getMeeting(this.roomId);
    } catch (err) {
      this.errorMessage = err instanceof Error ? err.message : 'Meeting not found';
    }

    // Default friendly random guest name
    if (!this.displayName) {
      const adjectives = ['Cosmic', 'Swift', 'Bright', 'Clever', 'Agile', 'Vibrant', 'Serene'];
      const nouns = ['Voyager', 'Falcon', 'Pioneer', 'Echo', 'Phoenix', 'Horizon', 'Orbit'];
      const rAdj = adjectives[Math.floor(Math.random() * adjectives.length)];
      const rNoun = nouns[Math.floor(Math.random() * nouns.length)];
      this.displayName = `${rAdj} ${rNoun}`;
    }

    this.render(container);
    await this.startMediaPreview(container);

    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('autojoin') === '1' && !this.errorMessage) {
        setTimeout(() => {
          this.executeJoin(container);
        }, 300);
      }
    } catch (_) {}
  }

  public render(container: HTMLElement) {
    if (this.errorMessage) {
      container.innerHTML = `
        <div class="lobby-container" style="display: flex; align-items: center; justify-content: center; min-height: 80vh;">
          <div class="card" style="max-width: 480px; width: 90%; text-align: center; padding: 2.5rem 2rem;">
            <div style="width: 56px; height: 56px; border-radius: 50%; background: rgba(239, 68, 68, 0.15); color: var(--color-danger); display: flex; align-items: center; justify-content: center; margin: 0 auto 1.25rem;">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>
            <h2 style="font-size: 1.4rem; font-weight: 700; margin-bottom: 0.5rem;">Meeting Ended or Expired</h2>
            <p style="color: var(--text-secondary); font-size: 0.95rem; margin-bottom: 2rem;">
              This meeting has ended or the link is no longer valid. Meeting links are removed immediately to free backend resources.
            </p>
            <button id="btn-err-home" class="btn-primary" style="width: 100%; justify-content: center;">
              Return to Home
            </button>
          </div>
        </div>
      `;
      container.querySelector('#btn-err-home')?.addEventListener('click', () => {
        if (this.onBack) this.onBack();
      });
      return;
    }

    const isAvatarDisabled = this.meetingInfo?.avatar_policy === 'disabled';
    const isAvatarRequired = this.meetingInfo?.avatar_policy === 'required';

    container.innerHTML = `
      <div class="lobby-container">
        <div class="lobby-card">
          <!-- Left: Camera Preview & Controls -->
          <div>
            <div class="lobby-preview-wrapper">
              <video id="lobby-preview-video" class="lobby-video" autoplay playsinline muted></video>
              <div id="video-off-placeholder" class="lobby-video-placeholder" style="display: ${this.isVideoMuted ? 'flex' : 'none'};">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v1"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
                <span>Camera is turned off</span>
              </div>

              <div class="lobby-controls-overlay">
                <button id="btn-toggle-mic" class="control-btn ${this.isAudioMuted ? 'active-danger' : ''}">
                  ${this.isAudioMuted ? `
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></line>
                      <line x1="12" y1="19" x2="12" y2="23"></line>
                      <line x1="8" y1="23" x2="16" y2="23"></line>
                    </svg>
                  ` : `
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                      <line x1="12" y1="19" x2="12" y2="23"></line>
                      <line x1="8" y1="23" x2="16" y2="23"></line>
                    </svg>
                  `}
                </button>

                <button id="btn-toggle-cam" class="control-btn ${this.isVideoMuted ? 'active-danger' : ''}">
                  ${this.isVideoMuted ? `
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                      <path d="M21 21l-3.34-3.34M23 7l-7 5 7 5V7z"></path>
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                    </svg>
                  ` : `
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polygon points="23 7 16 12 23 17 23 7"></polygon>
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                    </svg>
                  `}
                </button>
              </div>
            </div>

            <!-- Real-time Volume Level Meter -->
            <div class="meter-container">
              <div style="display: flex; justify-content: space-between; font-size: 0.75rem; color: var(--text-muted);">
                <span>Microphone Level</span>
                <span id="meter-level-val">Ready</span>
              </div>
              <div class="meter-bar">
                <div id="meter-fill" class="meter-fill"></div>
              </div>
            </div>

            <!-- Device Selection -->
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 1rem;">
              <select id="select-camera" class="form-input" style="font-size: 0.8rem; padding: 8px 10px;">
                <option value="">${t('cameraDefault')}</option>
              </select>
              <select id="select-mic" class="form-input" style="font-size: 0.8rem; padding: 8px 10px;">
                <option value="">${t('micDefault')}</option>
              </select>
            </div>
          </div>

          <!-- Right: Meeting Metadata & Join Form -->
          <div style="display: flex; flex-direction: column; justify-content: center;">
            <div style="margin-bottom: 1.5rem;">
              <div class="badge ${this.hostToken ? 'badge-host' : 'badge-moderator'}" style="margin-bottom: 6px;">
                ${this.hostToken ? 'You are the Host' : 'Participant'}
              </div>
              <h2>${this.meetingInfo?.title || 'Verve Meeting'}</h2>
              <p style="font-size: 0.875rem; color: var(--text-secondary); margin-top: 4px;">
                Room ID: <span style="font-family: var(--font-mono);">${this.roomId}</span>
              </p>
            </div>

            ${this.errorMessage ? `
              <div style="background: var(--color-danger-bg); border: 1px solid var(--color-danger); color: var(--color-danger); padding: 10px 14px; border-radius: var(--radius-md); font-size: 0.875rem; margin-bottom: 1rem;">
                ${this.errorMessage}
              </div>
            ` : ''}

            <form id="form-enter-room">
              <div class="form-group">
                <label class="form-label">${t('displayName')}</label>
                <input type="text" id="input-display-name" class="form-input" value="${this.displayName}" required maxlength="40" />
              </div>

              ${!isAvatarDisabled ? `
                <div class="form-group avatar-upload-section" style="margin-bottom: 1.25rem;">
                  <label class="form-label" style="display: flex; justify-content: space-between; align-items: center;">
                    <span>${t('profilePicture')} ${isAvatarRequired ? '<span style="color: var(--color-danger)">* (Required)</span>' : '<span style="color: var(--text-muted); font-size: 0.75rem;">(Optional)</span>'}</span>
                    ${this.avatarUrl ? `<button type="button" id="btn-remove-avatar" style="background: none; border: none; color: var(--color-danger); font-size: 0.75rem; cursor: pointer; padding: 0;">${t('removePhoto')}</button>` : ''}
                  </label>
                  <div style="display: flex; align-items: center; gap: 14px; margin-top: 6px;">
                    <div class="avatar-preview-circle" style="width: 52px; height: 52px; border-radius: 50%; background: var(--brand-gradient); display: flex; align-items: center; justify-content: center; overflow: hidden; border: 2px solid var(--border-color); flex-shrink: 0;">
                      ${this.avatarUrl ? `<img src="${this.avatarUrl}" style="width: 100%; height: 100%; object-fit: cover;" />` : `<span style="font-size: 1.1rem; font-weight: 700; color: white;">${this.displayName.slice(0, 2).toUpperCase() || 'U'}</span>`}
                    </div>
                    <div style="display: flex; gap: 8px; flex-wrap: wrap;">
                      <label class="btn-secondary" style="font-size: 0.8rem; padding: 6px 12px; cursor: pointer; margin: 0; display: inline-flex; align-items: center; gap: 6px;">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                        <span>${t('uploadPhoto')}</span>
                        <input type="file" id="input-avatar-file" accept="image/*" style="display: none;" />
                      </label>
                      <button type="button" id="btn-snap-avatar" class="btn-secondary" style="font-size: 0.8rem; padding: 6px 12px; display: inline-flex; align-items: center; gap: 6px;">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
                        <span>${t('snapCamera')}</span>
                      </button>
                    </div>
                  </div>
                </div>
              ` : ''}

              ${this.meetingInfo?.requires_passcode && !this.hostToken ? `
                <div class="form-group">
                  <label class="form-label">${t('meetingPasscode')}</label>
                  <input type="password" id="input-room-passcode" class="form-input" placeholder="Enter required passcode" required />
                </div>
              ` : ''}

              <button type="submit" id="btn-submit-enter" class="btn-primary" style="margin-top: 0.5rem;" ${this.isJoining ? 'disabled' : ''}>
                ${this.isJoining ? t('joining') : `
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                  </svg>
                  ${t('enterMeeting')}
                `}
              </button>

              <button type="button" id="btn-cancel-lobby" class="btn-secondary" style="width: 100%; margin-top: 8px; justify-content: center;">
                ${t('backToHome')}
              </button>
            </form>
          </div>
        </div>
      </div>
    `;

    this.bindEvents(container);
  }

  private async startMediaPreview(container: HTMLElement) {
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
      });

      const videoEl = container.querySelector('#lobby-preview-video') as HTMLVideoElement;
      if (videoEl && this.localStream) {
        videoEl.srcObject = this.localStream;
      }

      // Connect Audio Analyzer
      this.audioAnalyzer.attachStream(this.localStream);
      this.audioAnalyzer.onVolume = (vol) => {
        const fill = container.querySelector('#meter-fill') as HTMLElement;
        if (fill) {
          fill.style.width = `${Math.min(100, Math.round(vol * 200))}%`;
        }
      };

      // Populate Device Lists
      const devices = await navigator.mediaDevices.enumerateDevices();
      const camSelect = container.querySelector('#select-camera') as HTMLSelectElement;
      const micSelect = container.querySelector('#select-mic') as HTMLSelectElement;

      if (camSelect && micSelect) {
        camSelect.innerHTML = '';
        micSelect.innerHTML = '';

        devices.forEach(d => {
          const opt = document.createElement('option');
          opt.value = d.deviceId;
          opt.text = d.label || `${d.kind} (${d.deviceId.slice(0, 5)})`;

          if (d.kind === 'videoinput') camSelect.appendChild(opt);
          if (d.kind === 'audioinput') micSelect.appendChild(opt);
        });
      }
    } catch (err) {
      console.warn('Local preview access blocked or unavailable:', err);
      const placeholder = container.querySelector('#video-off-placeholder') as HTMLElement;
      if (placeholder) {
        placeholder.style.display = 'flex';
        placeholder.querySelector('span')!.innerText = 'Camera access blocked or not detected';
      }
    }
  }

  private bindEvents(container: HTMLElement) {
    container.querySelector('#btn-toggle-mic')?.addEventListener('click', () => {
      this.isAudioMuted = !this.isAudioMuted;
      if (this.localStream) {
        this.localStream.getAudioTracks().forEach(t => (t.enabled = !this.isAudioMuted));
      }
      const micBtn = container.querySelector('#btn-toggle-mic') as HTMLElement;
      if (micBtn) {
        if (this.isAudioMuted) {
          micBtn.classList.add('active-danger');
          micBtn.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="1" y1="1" x2="23" y2="23"></line>
              <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
              <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></line>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </svg>
          `;
        } else {
          micBtn.classList.remove('active-danger');
          micBtn.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </svg>
          `;
        }
      }
    });

    container.querySelector('#btn-toggle-cam')?.addEventListener('click', async () => {
      this.isVideoMuted = !this.isVideoMuted;
      const camBtn = container.querySelector('#btn-toggle-cam') as HTMLElement;
      const placeholder = container.querySelector('#video-off-placeholder') as HTMLElement;
      const videoEl = container.querySelector('#lobby-preview-video') as HTMLVideoElement;

      if (this.isVideoMuted) {
        if (this.localStream) {
          this.localStream.getVideoTracks().forEach(t => (t.enabled = false));
        }
        if (camBtn) {
          camBtn.classList.add('active-danger');
          camBtn.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="1" y1="1" x2="23" y2="23"></line>
              <path d="M21 21l-3.34-3.34M23 7l-7 5 7 5V7z"></path>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
            </svg>
          `;
        }
        if (placeholder) placeholder.style.display = 'flex';
      } else {
        let liveVideo = this.localStream?.getVideoTracks().find(t => t.readyState === 'live');
        if (liveVideo) {
          liveVideo.enabled = true;
        } else {
          try {
            const camStream = await navigator.mediaDevices.getUserMedia({
              video: { width: { ideal: 1280 }, height: { ideal: 720 } },
            });
            const newTrack = camStream.getVideoTracks()[0];
            if (newTrack) {
              if (!this.localStream) this.localStream = new MediaStream();
              this.localStream.addTrack(newTrack);
            }
          } catch (e) {
            console.warn('Failed to start camera preview:', e);
          }
        }
        if (videoEl && this.localStream) {
          videoEl.srcObject = this.localStream;
          videoEl.play().catch(() => {});
        }
        if (camBtn) {
          camBtn.classList.remove('active-danger');
          camBtn.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polygon points="23 7 16 12 23 17 23 7"></polygon>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
            </svg>
          `;
        }
        if (placeholder) placeholder.style.display = 'none';
      }
    });

    // Avatar upload handler
    const fileInput = container.querySelector('#input-avatar-file') as HTMLInputElement;
    fileInput?.addEventListener('change', () => {
      const file = fileInput.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (re) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxDim = 160;
          let w = img.width;
          let h = img.height;
          if (w > h) {
            h = Math.round((h * maxDim) / w);
            w = maxDim;
          } else {
            w = Math.round((w * maxDim) / h);
            h = maxDim;
          }
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, w, h);
            this.avatarUrl = canvas.toDataURL('image/jpeg', 0.85);
            this.clearValidationError(container);
            this.render(container);
            this.startMediaPreview(container);
          }
        };
        img.src = re.target?.result as string;
      };
      reader.readAsDataURL(file);
    });

    // Snap Camera handler
    container.querySelector('#btn-snap-avatar')?.addEventListener('click', () => {
      const videoEl = container.querySelector('#lobby-preview-video') as HTMLVideoElement;
      if (!videoEl || videoEl.videoWidth === 0) {
        alert('Please turn on your camera to take a snapshot.');
        return;
      }

      const canvas = document.createElement('canvas');
      const maxDim = 160;
      let w = videoEl.videoWidth;
      let h = videoEl.videoHeight;
      if (w > h) {
        h = Math.round((h * maxDim) / w);
        w = maxDim;
      } else {
        w = Math.round((w * maxDim) / h);
        h = maxDim;
      }
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(videoEl, 0, 0, w, h);
        this.avatarUrl = canvas.toDataURL('image/jpeg', 0.85);
        this.clearValidationError(container);
        this.render(container);
        this.startMediaPreview(container);
      }
    });

    // Remove Avatar handler
    container.querySelector('#btn-remove-avatar')?.addEventListener('click', () => {
      this.avatarUrl = '';
      this.render(container);
      this.startMediaPreview(container);
    });

    const triggerJoin = async () => {
      const nameInput = container.querySelector('#input-display-name') as HTMLInputElement;
      const passInput = container.querySelector('#input-room-passcode') as HTMLInputElement;

      this.displayName = nameInput?.value?.trim() || this.displayName;
      this.passcode = passInput?.value?.trim() || '';

      if (!this.displayName) {
        this.showValidationError(container, 'Please enter a display name.');
        return;
      }

      await this.executeJoin(container);
    };

    container.querySelector('#form-enter-room')?.addEventListener('submit', async (e) => {
      e.preventDefault();
      await triggerJoin();
    });

    container.querySelector('#btn-submit-enter')?.addEventListener('click', async (e) => {
      e.preventDefault();
      await triggerJoin();
    });

    container.querySelector('#btn-cancel-lobby')?.addEventListener('click', () => {
      this.cleanup();
      if (this.onBack) this.onBack();
    });
  }

  private showValidationError(container: HTMLElement, message: string) {
    this.errorMessage = message;
    let errBox = container.querySelector('#lobby-error-msg') as HTMLElement;
    if (!errBox) {
      const form = container.querySelector('#form-enter-room');
      if (form) {
        errBox = document.createElement('div');
        errBox.id = 'lobby-error-msg';
        form.parentNode?.insertBefore(errBox, form);
      }
    }
    if (errBox) {
      errBox.style.display = 'block';
      errBox.style.background = 'rgba(239, 68, 68, 0.15)';
      errBox.style.border = '1px solid var(--color-danger)';
      errBox.style.color = 'var(--color-danger)';
      errBox.style.padding = '10px 14px';
      errBox.style.borderRadius = 'var(--radius-md)';
      errBox.style.fontSize = '0.875rem';
      errBox.style.marginBottom = '1rem';
      errBox.innerHTML = `⚠️ ${message}`;
    }

    const uploadSection = container.querySelector('.avatar-upload-section') as HTMLElement;
    if (uploadSection) {
      uploadSection.style.border = '1px solid var(--color-danger)';
      uploadSection.style.borderRadius = 'var(--radius-md)';
      uploadSection.style.padding = '8px';
      uploadSection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  private clearValidationError(container: HTMLElement) {
    this.errorMessage = '';
    const errBox = container.querySelector('#lobby-error-msg') as HTMLElement;
    if (errBox) {
      errBox.style.display = 'none';
      errBox.innerHTML = '';
    }
    const uploadSection = container.querySelector('.avatar-upload-section') as HTMLElement;
    if (uploadSection) {
      uploadSection.style.border = 'none';
      uploadSection.style.padding = '0';
    }
  }

  private async executeJoin(container: HTMLElement) {
    // If meeting requires avatar and none uploaded, block immediately on button click
    const isRequired = this.meetingInfo?.avatar_policy === 'required';
    if (isRequired && !this.avatarUrl) {
      this.showValidationError(
        container,
        'A profile picture is required by the host to join this meeting. Please upload a photo or snap one with your camera.'
      );
      return;
    }

    this.clearValidationError(container);
    this.isJoining = true;

    const submitBtn = container.querySelector('#btn-submit-enter') as HTMLButtonElement;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerText = 'Joining...';
    }

    try {
      const resp = await ApiService.joinMeeting(
        this.roomId,
        this.displayName,
        this.passcode,
        this.hostToken,
        this.avatarUrl
      );

      this.audioAnalyzer.stop();
      if (this.onJoinSuccess) {
        this.onJoinSuccess(resp, this.localStream, this.isVideoMuted, this.isAudioMuted);
      }
    } catch (err) {
      this.isJoining = false;
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polygon points="5 3 19 12 5 21 5 3"></polygon>
          </svg>
          Enter Meeting
        `;
      }
      const msg = err instanceof Error ? err.message : 'Failed to join meeting';
      this.showValidationError(container, msg);
    }
  }

  public detachMedia() {
    this.audioAnalyzer.stop();
    this.localStream = null;
  }

  public cleanup() {
    this.audioAnalyzer.stop();
    if (this.localStream) {
      this.localStream.getTracks().forEach(t => t.stop());
      this.localStream = null;
    }
  }
}
